import type { World } from '../sim/types';
import { ISLAND_NAMES, REEF_ISLANDS, type ReefIsland } from '../sim/reef';
import { anyProtectionJob, isSurveyHome } from '../sim/operations';
import { PROTECTED_HERBIVORES } from '../sim/management';
import { compare, lastSurvey, summed, surveyCounts, type Change } from './reports';
import { latestChange } from './reefHealth';
import { managementOf } from './overview';

/**
 * What a survey's findings change (M330, `docs/ONE_REEF_ROADMAP.md`'s O5, the playtest's: "Neither result made me
 * reconsider the protection plan I had already started"): the next step read from what the last survey home counted,
 * so two reefs whose surveys find different things are offered different steps. GAME CHOICE: the thresholds are the
 * counts' own errors, the findings page's and the coral report's (`compare`'s two standard errors of the log ratio,
 * `latestChange`'s twice the combined error), no new figure:
 *
 * - a coral count that found one island's coral fallen past the error since the count before: a closer count there, the
 *   boat's sites all on that island's reef (four islands' sites on one, so its error about half, `areaCounts`);
 * - a coral count with no such fall, the grazing fish never counted by the conservancy: their count, the baseline a
 *   protection is judged against;
 * - a grazing fish count that found fewer than the count before, past the error: the protected area;
 *   with one in force or under way, a patrol to keep it; one that found them holding within the error, nothing yet
 *   protecting them: the protected area, the count its baseline;
 * - otherwise none (the fish risen past the error, or protected and holding): the standing words.
 */
export type StepKind = 'closer' | 'fishCount' | 'protect' | 'patrol';
export interface Step {
  kind: StepKind;
  /** The island a closer count goes to; none for the reef's own. */
  island: ReefIsland | null;
  label: string;
  /** The HUD act that lights it (`reef:<what>[:<kind>[:<island>]]`). */
  act: string;
  /** Why, from what the survey found. */
  why: string;
  /** The survey home it is read from, its day. */
  day: number;
}

/** The grazing fish a fish count found on a day against the count before it, summed (the findings page's); none where it was the first. */
function grazingChange(world: World, day: number): Change | null {
  const both = surveyCounts(world, 'reefFish', day).filter((r) => (PROTECTED_HERBIVORES as readonly string[]).includes(r.id) && r.before !== null);
  return both.length === 0 ? null : compare(summed(both.map((r) => r.before as { value: number; cv: number | null })), summed(both.map((r) => r.now)));
}

/** The islands whose coral the conservancy's count of a day found fallen past the error since the count before, the largest fall first. */
function fallenAt(world: World, day: number): { island: ReefIsland; from: number; to: number }[] {
  return REEF_ISLANDS.flatMap((island) => {
    const c = latestChange(world, island, day);
    return c && c.trend === 'down' && c.last.day === day ? [{ island, from: c.before.value, to: c.last.value }] : [];
  }).sort((a, b) => (b.from - b.to) - (a.from - a.to));
}

/** The next step the last survey's findings call for, while it is still to take; none where they call for none. */
export function stepFromFindings(world: World): Step | null {
  const last = lastSurvey(world);
  if (!last) return null;
  const ops = world.operations;
  const m = managementOf(world);
  const protecting = (m?.islands.every((x) => x.herbivoresProtected) ?? false) || anyProtectionJob(ops) !== undefined;
  // A trip of a kind sent since the survey came home (at sea or planned; one home since is the last survey itself), to
  // an island or the whole reef.
  const sentSince = (kind: string, island: ReefIsland | null): boolean => ops.plan.some((p) => p.kind === kind && p.island === island)
    || ops.jobs.some((j) => j.survey === kind && j.island === island);
  // The last survey's own island, where it counted one alone (the closer count's): its findings that island's only.
  const home = ops.finished.filter((f) => isSurveyHome(f) && f.endDay === last.day).at(-1);
  const patrolled = (m?.islands.every((x) => x.patrol.level > 0) ?? false);
  const protect = (why: string): Step | null => (!protecting
    ? { kind: 'protect', island: null, label: 'Propose a protected area', act: 'reef:protect', why, day: last.day }
    : patrolled ? null : { kind: 'patrol', island: null, label: 'Patrol a stretch of coast', act: 'reef:patrol', why: `${why} A protected area is in force or under way: a patrol keeps its rule.`, day: last.day });
  if (last.kind === 'reefBenthic') {
    // A count of one island (the closer count) answers its own fall, the rest of the reef not counted: no step (its
    // review: "No island's coral fell" had been told of the three not counted).
    if (home?.island) return null;
    const fell = fallenAt(world, last.day)[0];
    if (fell) {
      const name = ISLAND_NAMES[fell.island];
      return sentSince('reefBenthic', fell.island) ? null : {
        kind: 'closer', island: fell.island, label: `Count ${name}'s coral again`, act: `reef:survey:reefBenthic:${fell.island}`, day: last.day,
        why: `${name}'s coral fell from ${fell.from.toFixed(1)}% to ${fell.to.toFixed(1)}%, past the counts' error: a second count of its reef tells whether the fall holds.`,
      };
    }
    // The conservancy's own grazing fish counts, kept beyond the trips' year (`AREA_COUNTS_KEPT`; its review: the
    // finished trips are pruned to a year).
    const fishCounted = ops.areaCounts.some((c) => c.kind === 'herbivores' && c.by !== 'noaa');
    if (!fishCounted) {
      return sentSince('reefFish', null) ? null : {
        kind: 'fishCount', island: null, label: 'Count its grazing fish', act: 'reef:survey:reefFish', day: last.day,
        why: 'No island\'s coral fell past the counts\' error. The grazing fish that keep the algae off it have no count of yours: count them, the baseline a protection is judged by.',
      };
    }
    return null;
  }
  if (last.kind === 'reefFish') {
    const change = grazingChange(world, last.day);
    // Whose last count it is, the record the change is set against: the conservancy's whole reef's on its day, else NOAA's
    // divers' (M336, C1: "the last count" beside "our first survey" had read as the player's own; its review: any earlier
    // count of the conservancy's, an island's or one before NOAA's next, had been told as the one compared).
    const beforeDay = Math.max(-Infinity, ...surveyCounts(world, 'reefFish', last.day).flatMap((r) => (r.before ? [r.before.day] : [])));
    const ours = ops.areaCounts.filter((c) => c.kind === 'herbivores' && c.by === 'conservancy' && c.day === beforeDay).length === REEF_ISLANDS.length;
    const whose = ours ? 'your last count' : 'NOAA\'s divers\' last count';
    if (change?.words === 'fewer than') return protect(`Fewer grazing fish than at ${whose}, ${Math.round(-change.ratio * 100)}% fewer, past the counts' error: the fishing on them is the lever you hold.`);
    // Holding within the error, nothing yet protecting them: the count is the protection's baseline. Risen past it: none.
    // NOAA's count before every survey of the game's (its review: a first count, none before it, cannot come).
    if (change?.words === 'about the same as' && !protecting) return protect(`About as many grazing fish as at ${whose}, within the counts' error. Your count is the starting point a protection is measured against.`);
    return null;
  }
  return null;
}
